import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Download, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { finalCTA, submitCollaborationForm } from '../data/mock';

const FinalCTA = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const sectionRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await submitCollaborationForm(formData);
      if (result.success) {
        toast({
          title: "Success!",
          description: result.message,
        });
        setFormData({ name: '', email: '', company: '', message: '' });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMediaKit = () => {
    toast({
      title: "Media Kit",
      description: "Media kit download will be available soon!",
    });
  };

  return (
    <section 
      id="contact" 
      ref={sectionRef} 
      className="py-24 bg-gradient-to-br from-[#2563EB]/10 via-[#0B0B0B] to-[#2563EB]/5 relative overflow-hidden"
    >
      {/* Animated background gradient */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(37, 99, 235, 0.15) 0%, transparent 50%)',
          animation: 'pulse-slow 8s ease-in-out infinite'
        }}
      />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.8s ease-out'
            }}
          >
            {finalCTA.headline}
          </h2>
          <p 
            className="text-lg text-white/70 leading-relaxed"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.8s ease-out 0.2s'
            }}
          >
            {finalCTA.description}
          </p>
        </div>

        {/* Form */}
        <div 
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 shadow-2xl"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'scale(1)' : 'scale(0.95)',
            transition: 'all 0.8s ease-out 0.4s'
          }}
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  Name *
                </label>
                <Input
                  required
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#2563EB] transition-colors duration-200"
                />
              </div>
              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  Email *
                </label>
                <Input
                  required
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#2563EB] transition-colors duration-200"
                />
              </div>
            </div>

            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Company/Brand *
              </label>
              <Input
                required
                placeholder="Your company name"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#2563EB] transition-colors duration-200"
              />
            </div>

            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Message *
              </label>
              <Textarea
                required
                placeholder="Tell us about your campaign goals..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#2563EB] min-h-32 transition-colors duration-200"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-[#2563EB] hover:bg-[#1d4ed8] text-white py-6 text-lg transition-all duration-300 shadow-xl hover:shadow-[#2563EB]/50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={20} />
                    Sending...
                  </>
                ) : (
                  <>
                    {finalCTA.primaryCTA}
                    <ArrowRight className="ml-2" size={20} />
                  </>
                )}
              </Button>
              <Button
                type="button"
                onClick={handleMediaKit}
                variant="outline"
                className="flex-1 border-white/20 text-white hover:bg-white/10 py-6 text-lg transition-colors duration-200"
              >
                <Download className="mr-2" size={20} />
                {finalCTA.secondaryCTA}
              </Button>
            </div>
          </form>
        </div>
      </div>

      <style>{`
        @keyframes pulse-slow {
          0%, 100% { transform: scale(1); opacity: 0.3; }
          50% { transform: scale(1.1); opacity: 0.5; }
        }
      `}</style>
    </section>
  );
};

export default FinalCTA;
